
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Headphones, Film, FileText } from "lucide-react";
import PodcastCard from './PodcastCard';
import VideoCard from './VideoCard';
import SummaryCard from './SummaryCard';

interface ContentTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  filteredPodcasts: any[];
  filteredVideos: any[];
  filteredSummaries: any[];
  hasContent: boolean;
}

const ContentTabs = ({ 
  activeTab, 
  setActiveTab, 
  filteredPodcasts, 
  filteredVideos, 
  filteredSummaries,
  hasContent
}: ContentTabsProps) => {
  
  return (
    <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="w-full grid grid-cols-4 mb-8">
        <TabsTrigger value="all" className="rounded-md data-[state=active]:bg-nation-purple data-[state=active]:text-white">
          All
        </TabsTrigger>
        <TabsTrigger value="podcasts" className="rounded-md data-[state=active]:bg-nation-purple data-[state=active]:text-white">
          <Headphones size={16} className="mr-2" />
          Podcasts
        </TabsTrigger>
        <TabsTrigger value="videos" className="rounded-md data-[state=active]:bg-nation-purple data-[state=active]:text-white">
          <Film size={16} className="mr-2" />
          Videos
        </TabsTrigger>
        <TabsTrigger value="summaries" className="rounded-md data-[state=active]:bg-nation-purple data-[state=active]:text-white">
          <FileText size={16} className="mr-2" />
          Summaries
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="all" className="space-y-4">
        {!hasContent ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No results found. Try adjusting your search.</p>
          </div>
        ) : (
          <>
            {filteredPodcasts.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
                  <Headphones size={20} className="mr-2" />
                  Podcasts
                </h2>
                {filteredPodcasts.slice(0, 2).map((podcast, i) => (
                  <PodcastCard key={i} podcast={podcast} />
                ))}
                {filteredPodcasts.length > 2 && (
                  <div className="text-center mt-4">
                    <Button variant="outline" onClick={() => setActiveTab("podcasts")}>
                      View All Podcasts
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {filteredVideos.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
                  <Film size={20} className="mr-2" />
                  Videos
                </h2>
                {filteredVideos.slice(0, 2).map((video, i) => (
                  <VideoCard key={i} video={video} />
                ))}
                {filteredVideos.length > 2 && (
                  <div className="text-center mt-4">
                    <Button variant="outline" onClick={() => setActiveTab("videos")}>
                      View All Videos
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {filteredSummaries.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
                  <FileText size={20} className="mr-2" />
                  Document Summaries
                </h2>
                {filteredSummaries.slice(0, 2).map((summary, i) => (
                  <SummaryCard key={i} summary={summary} />
                ))}
                {filteredSummaries.length > 2 && (
                  <div className="text-center mt-4">
                    <Button variant="outline" onClick={() => setActiveTab("summaries")}>
                      View All Summaries
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </TabsContent>
      
      <TabsContent value="podcasts">
        <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
          <Headphones size={20} className="mr-2" />
          Podcasts
        </h2>
        {filteredPodcasts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No podcasts found. Try adjusting your search.</p>
          </div>
        ) : (
          filteredPodcasts.map((podcast, i) => (
            <PodcastCard key={i} podcast={podcast} />
          ))
        )}
      </TabsContent>
      
      <TabsContent value="videos">
        <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
          <Film size={20} className="mr-2" />
          Videos
        </h2>
        {filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No videos found. Try adjusting your search.</p>
          </div>
        ) : (
          filteredVideos.map((video, i) => (
            <VideoCard key={i} video={video} />
          ))
        )}
      </TabsContent>
      
      <TabsContent value="summaries">
        <h2 className="text-xl font-semibold text-nation-blue mb-4 flex items-center">
          <FileText size={20} className="mr-2" />
          Document Summaries
        </h2>
        {filteredSummaries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No summaries found. Try adjusting your search.</p>
          </div>
        ) : (
          filteredSummaries.map((summary, i) => (
            <SummaryCard key={i} summary={summary} />
          ))
        )}
      </TabsContent>
    </Tabs>
  );
};

export default ContentTabs;
